import { ArchiveBook, GlobalSearch } from "iconsax-react";
import {
  ArrowUpDown,
  CircleHelp,
  FileChartColumn,
  Settings,
  Link2,
  Ticket,
} from "lucide-react";
import { useAuth } from "../../app/hooks/useAuth";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { bannerService } from "@/app/services/bannerService";
import { BannerType } from "@/app/entities/Banner";
import { localStorageKeys } from "@/app/config/localStorageKeys";
import { useWhitelabelContext } from "@/app/contexts/whitelabelContext";

export default function usePrivateLayout() {
  const { appConfig } = useWhitelabelContext();

  const [modalOpen, setModalOpen] = useState(false);
  const {
    isSuperAdmin,
    isAdmin,
    isLeader,
    isManager,
    isSupport,
    isSupervisor,
    isSeller,
    user,
  } = useAuth();

  const { data: banner } = useQuery({
    queryKey: ["getBanners", "dashboardModal"],
    queryFn: () => bannerService.getBanners(BannerType.DASHBOARD_MODAL),
    select(data) {
      return {
        ...data.banners[0],
        // title: undefined,
        // description: undefined,
        // buttonLabel: undefined,
        // link: undefined,
        // videoUrl: data.banners[0].link,
        // videoUrl: undefined,
        // uri: undefined,
      };
    },
  });

  useEffect(() => {
    const checkModalStatus = async () => {
      const modalCached = localStorage.getItem(
        localStorageKeys.DASHBOARD_MODAL,
      );
      if (!modalCached || modalCached !== JSON.stringify(banner)) {
        setModalOpen(true);
      }
    };

    if (!(isSuperAdmin || isAdmin || isSupport) && banner?.uri)
      checkModalStatus();
  }, [isAdmin, isSupport, banner, isSuperAdmin]);

  const _: Record<string, any> = {
    transactions: {
      label: "Transações",
      path: "/transacoes",
      icon: <ArrowUpDown />,
    },
    managers: {
      label: "Gerentes",
      path: "/gerentes",
      icon: <i className="fa-solid fa-user-tie" />,
    },
    supervisors: {
      label: "Supervisores",
      path: "/supervisores",
      icon: <i className="fa-solid fa-user-tie" />,
    },
    teams: {
      label: "Equipes",
      path: "/times",
      icon: <i className="fa-solid fa-people-group" />,
    },
    users: {
      label: "Vendedores",
      path: "/vendedores",
      icon: <i className="fa-solid fa-chalkboard-user" />,
    },
    consults: {
      label: "Radar",
      path: "/radar",
      icon: <GlobalSearch />,
    },
    clients: {
      label: "Clientes",
      path: "/clientes",
      icon: <i className="fa-solid fa-user" />,
    },
    radar: {
      label: `Radar`,
      path: "/radar",
      icon: <GlobalSearch />,
      isActive: !!user?.customerCount,
      count: user?.customerCount || 0,
    },
    links: {
      label: `Links`,
      path: "/links",
      icon: <Link2 className="-rotate-45" />,
    },
    leads: {
      label: `Leads`,
      path: "/leads",
      icon: <Link2 className="-rotate-45" />,
    },
    contentsAdmin: {
      label: "Conteúdos",
      path: "/conteudos/gerenciar",
      icon: <ArchiveBook />,
    },
    contents: {
      label: "Conteúdos",
      path: "/conteudos/listar",
      icon: <ArchiveBook />,
    },
    products: {
      label: "Produtos",
      path: "/produtos",
      icon: <i className="fa-solid fa-box" />,
    },
    myTeam: {
      label: "Minha equipe",
      path: `/meutime/${user?.myTeam?._id}`,
      icon: <i className="fa-solid fa-people-group" />,
    },
    raffle: {
      label: "Sorteios",
      path: "/sorteios",
      icon: <i className="fa-solid fa-trophy" />,
    },
    reports: {
      label: "Relatórios",
      path: "/relatorios",
      icon: <FileChartColumn />,
    },
    config: {
      label: "Configurações",
      path: "/configuracoes",
      icon: <Settings />,
    },
    support: {
      label: "Suporte",
      path: "/support",
      icon: <CircleHelp />,
    },
    mySupportRequests: {
      label: "Meus chamados",
      path: "/supports",
      icon: <Ticket />,
    },
    supportRequests: {
      label: "Chamados",
      path: "/supports",
      icon: <Ticket />,
    },
  };

  const isAdminRole = isSuperAdmin || isAdmin || isSupport;
  const contentMenu = appConfig?.content
    ? [isAdminRole ? _.contentsAdmin : _.contents]
    : [];

  const menus = [];
  if (isSuperAdmin)
    menus.push(
      ...(appConfig?.SHOW_RADAR_PAGE ? [_.consults] : []),
      _.transactions,
      ...(appConfig?.SHOW_MANAGER_PAGE ? [_.managers] : []),
      _.teams,
      _.users,
      _.clients,
      ...contentMenu,
      ...(appConfig?.prizes ? [_.raffle] : []),
      _.reports,
      _.supportRequests,
      _.config,
    );

  if (isAdmin && !isSuperAdmin)
    menus.push(
      ...(appConfig?.SHOW_RADAR_PAGE ? [_.consults] : []),
      _.transactions,
      ...(appConfig?.SHOW_MANAGER_PAGE ? [_.managers] : []),
      _.teams,
      _.users,
      _.clients,
      ...contentMenu,
      ...(appConfig?.prizes ? [_.raffle] : []),
      _.reports,
      _.supportRequests,
      _.config,
    );

  if (isManager)
    menus.push(
      ...(appConfig?.SHOW_RADAR_PAGE ? [_.radar] : []),
      _.transactions,
      _.supervisors,
      _.teams,
      _.users,
      _.clients,
      ...(appConfig?.SHOW_LEADS_PAGE ? [_.leads] : []),
      ...(appConfig?.links ? [_.links] : []),
      ...contentMenu,
      ...(appConfig?.prizes ? [_.raffle] : []),
      _.reports,
      _.mySupportRequests,
    );

  if (isSupervisor)
    menus.push(
      ...(appConfig?.SHOW_RADAR_PAGE ? [_.radar] : []),
      _.transactions,
      _.teams,
      _.users,
      _.clients,
      ...(appConfig?.links ? [_.links] : []),
      ...(appConfig?.SHOW_LEADS_PAGE ? [_.leads] : []),
      ...(user?.myTeam?._id ? [_.myTeam] : []),
      ...contentMenu,
      ...(appConfig?.prizes ? [_.raffle] : []),
      _.reports,
      _.mySupportRequests,
    );

  if (isLeader)
    menus.push(
      ...(appConfig?.SHOW_RADAR_PAGE ? [_.radar] : []),
      _.transactions,
      _.users,
      _.clients,
      ...(appConfig?.links ? [_.links] : []),
      ...(appConfig?.SHOW_LEADS_PAGE ? [_.leads] : []),
      ...(user?.myTeam?._id ? [_.myTeam] : []),
      ...contentMenu,
      ...(appConfig?.prizes ? [_.raffle] : []),
      _.reports,
      _.mySupportRequests,
    );

  if (isSeller)
    menus.push(
      ...(appConfig?.SHOW_RADAR_PAGE ? [_.radar] : []),
      _.transactions,
      _.clients,
      ...(appConfig?.links ? [_.links] : []),
      ...(appConfig?.SHOW_LEADS_PAGE ? [_.leads] : []),
      ...contentMenu,
      ...(appConfig?.prizes ? [_.raffle] : []),
      _.reports,
      _.mySupportRequests,
    );

  if (isSupport) {
    const supportRequestsIndex = menus.findIndex(
      (menu) => menu.path === _.supportRequests.path,
    );

    if (supportRequestsIndex >= 0) {
      menus[supportRequestsIndex] = _.supportRequests;
    }

    const hasTeamsMenu = menus.some((menu) => menu.path === _.teams.path);

    if (!hasTeamsMenu) {
      const usersIndex = menus.findIndex((menu) => menu.path === _.users.path);

      if (usersIndex >= 0) menus.splice(usersIndex, 0, _.teams);
      else menus.push(_.teams);
    }

    menus.unshift(
      _.support,
      ...(supportRequestsIndex >= 0 ? [] : [_.supportRequests]),
    );
  }

  return {
    menus,
    modalState: {
      setOpen: setModalOpen,
      open: modalOpen,
    },
    banner,
  };
}
